import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import "./App.css";
import LandingPage from "./components/LandingPage";
import LoginPage from "./components/LoginPage";
import CreateAccountPage from "./components/CreateAccountPage";
import Dashboard from "./components/Dashboard";
import ClientCodeEntry from "./components/ClientCodeEntry";
import ClientForm from "./components/ClientForm";
import ConsultationFormPage from "./components/ConsultationFormPage";
import SubscriptionSelection from "./components/SubscriptionSelection";
import { useAuth } from "./hooks/useAuth";
import { apiClient } from "./services/api";
import ResetPasswordPage from "./components/ResetPasswordPage";
import { supabaseAuth } from "./supabaseClient";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import TeamPage from "./components/TeamPage";
import PublicIntakePage from './pages/PublicIntakePage';

function App() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showBetaCode, setShowBetaCode] = useState(false);
  const [betaCodeVerified, setBetaCodeVerified] = useState(false);
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  // Check for client form access
  const [isClientFormAccess, setIsClientFormAccess] = useState(false);
  const [clientCode, setClientCode] = useState("");

  useEffect(() => {
    const { data: listener } = supabaseAuth.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsPasswordRecovery(true);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Check if this is a client form access
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code) {
      setIsClientFormAccess(true);
      setClientCode(code);
    }
  }, []);

  // Check subscription status when user logs in
  useEffect(() => {
    if (user && !checkingSubscription) {
      checkSubscriptionStatus();
    }
  }, [user]);

  const checkSubscriptionStatus = async () => {
    // Prevent multiple simultaneous checks
    if (checkingSubscription) {
      return;
    }

    try {
      setCheckingSubscription(true);
      const response = await apiClient.get("/subscriptions/current");

      // User needs subscription if:
      // 1. No subscription exists (legacy user)
      // 2. Trial expired
      // 3. Subscription canceled and period ended
      if (response.needsSubscription || response.trialExpired) {
        console.log('User needs to select a subscription plan');
        setNeedsSubscription(true);
      } else {
        setNeedsSubscription(false);
      }
    } catch (error: any) {
      console.error("Error checking subscription:", error);
      // If 404 or no subscription found, they need to select a plan
      if (error.response?.status === 404 || error.response?.data?.needsSubscription) {
        setNeedsSubscription(true);
      } else {
        // Other errors, assume they need subscription to be safe
        setNeedsSubscription(true);
      }
    } finally {
      setCheckingSubscription(false);
      setSubscriptionChecked(true);
    }
  };

  const handleBackToLogin = () => {
    // If on a route, navigate to login route; otherwise use state
    if (location.pathname !== '/' && location.pathname !== '') {
      navigate('/login');
    } else {
      setShowCreateAccount(false);
      setShowLogin(true);
    }
  };

  const handleCreateAccount = () => {
    // If on a route, navigate to create account route; otherwise use state
    if (location.pathname === '/login' || location.pathname === '/signin') {
      navigate('/create-account');
    } else {
      setShowCreateAccount(true);
      setShowLogin(true);
    }
  };

  const handleSignIn = () => {
    // If on a route, navigate to login route; otherwise use state
    if (location.pathname !== '/' && location.pathname !== '') {
      navigate('/login');
    } else {
      setShowLogin(true);
    }
  };

  const handleBackToLanding = () => {
    // If on a route, navigate to home; otherwise use state
    if (location.pathname === '/login' || location.pathname === '/signin' || location.pathname === '/create-account') {
      navigate('/');
    } else {
      setShowLogin(false);
      setShowCreateAccount(false);
    }
  };

  const handleUserLogin = async (user: any) => {
    // User logged in successfully, proceed to dashboard
    console.log("User logged in successfully");
    // The useAuth hook will handle the user state update
  };

  const handleSubscriptionComplete = () => {
    setNeedsSubscription(false);
    // Refresh subscription status
    checkSubscriptionStatus();
  };

  if (loading || (user && !subscriptionChecked)) {
    return <div className="loading">Loading...</div>;
  }

  // Handle client form access
  if (isClientFormAccess) {
    if (clientCode) {
      return <ClientForm />;
    } else {
      return <ClientCodeEntry />;
    }
  }

  if (isPasswordRecovery) {
    return (
      <ResetPasswordPage onBackToLogin={() => setIsPasswordRecovery(false)} />
    );
  }

  return (
    <Routes>
      {/* Login/Signin routes - for marketing emails - MUST be before catch-all */}
      <Route
        path="/login"
        element={
          !user ? (
            <LoginPage
              onLogin={handleUserLogin}
              onCreateAccount={handleCreateAccount}
              onBackToHome={handleBackToLanding}
            />
          ) : (
            <Dashboard />
          )
        }
      />
      <Route
        path="/signin"
        element={
          !user ? (
            <LoginPage
              onLogin={handleUserLogin}
              onCreateAccount={handleCreateAccount}
              onBackToHome={handleBackToLanding}
            />
          ) : (
            <Dashboard />
          )
        }
      />
      {/* Create Account route */}
      <Route
        path="/create-account"
        element={
          !user ? (
            <CreateAccountPage onBackToLogin={handleBackToLogin} />
          ) : (
            <Dashboard />
          )
        }
      />

      {/* Public intake form route */}
      <Route path="/intake/:linkId" element={<PublicIntakePage />} />
      {/* Public consultation form route */}
      <Route path="/consultation/:templateId" element={<ConsultationFormPage />} />

      {/* Privacy Policy route */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />

      {/* Terms of Service route */}
      <Route path="/terms-of-service" element={<TermsOfService />} />

      {/* Team page route */}
      <Route path="/team" element={<TeamPage />} />

      {/* Main app routes - catch-all at the end */}
      <Route
        path="*"
        element={
          <div>
            {!user ? (
              showLogin ? (
                showCreateAccount ? (
                  <CreateAccountPage onBackToLogin={handleBackToLogin} />
                ) : (
                  <LoginPage
                    onLogin={handleUserLogin}
                    onCreateAccount={handleCreateAccount}
                    onBackToHome={handleBackToLanding}
                  />
                )
              ) : (
                <LandingPage onSignIn={handleSignIn} />
              )
            ) : needsSubscription ? (
              <SubscriptionSelection
                onSubscriptionComplete={handleSubscriptionComplete}
                isNewUser={false}
                onBackToHome={async () => {
                  // Sign out user to return to landing page
                  await supabaseAuth.auth.signOut();
                }}
              />
            ) : (
              <Dashboard />
            )}
          </div>
        }
      />
    </Routes>
  );
}

export default App;
